/**
 * Fetch wrapper for BFF pattern.
 *
 * All requests use `credentials: include` → browser auto-attaches the
 * HttpOnly session cookie. On 401 we redirect to backend login (which
 * handles the OAuth dance). No tokens in JS land.
 */

import { login } from "./auth.ts";

export interface ChatMessage {
	role: "user" | "assistant";
	content: string | unknown[];
}

export interface ChatRequest {
	messages: ChatMessage[];
	model?: string;
}

export interface ChatResponse {
	content: Array<{ type: string; text?: string; [key: string]: unknown }>;
	stopReason: string | null;
	usage: { inputTokens: number; outputTokens: number };
	model: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
	const headers = new Headers(init?.headers);
	if (!headers.has("Content-Type") && init?.body) {
		headers.set("Content-Type", "application/json");
	}

	const res = await fetch(path, {
		...init,
		headers,
		credentials: "include",
	});

	if (res.status === 401) {
		login();
		throw new Error("not_authenticated");
	}

	if (!res.ok) {
		const text = await res.text();
		// Try to extract a human-friendly message from JSON error body
		let friendly: string | null = null;
		try {
			const json = JSON.parse(text);
			if (typeof json.message === "string") friendly = json.message;
			else if (typeof json.error === "string") friendly = json.error;
		} catch {
			// not JSON, leave friendly null
		}
		const fallback = `${path} → ${res.status}: ${text.slice(0, 200)}`;
		throw new ApiError(res.status, friendly ?? fallback);
	}

	return (await res.json()) as T;
}

/**
 * Operator-gated reveal of the shared `g1-group-agent` client_credentials for
 * remote agent launch. Fetched on demand (never baked into the bundle) — see
 * B4-L. 401 → triggers re-login via the shared `request` helper.
 */
export async function getRunSecret(): Promise<{ clientId: string; clientSecret: string }> {
	return request("/api/agents/run-secret");
}

export interface DecisionDTO {
	id: string;
	summary: string;
	businesses: string[];
	category: string;
	followUpDate: string | null;
	gate: string | null;
	runwayMonths: number | null;
	nextDecisionDue: string | null;
	ts: string;
}

export type AuditEvent =
	| {
			kind: "tool_call";
			ts: number;
			userId: string;
			toolName: string;
			ok: boolean;
			latencyMs: number;
			inputPreview: string;
			outputPreview: string;
			outputBytes: number;
	  }
	| {
			kind: "chat_completion";
			ts: number;
			userId: string;
			model: string;
			inputTokens: number;
			outputTokens: number;
			estCostUsd: number;
			latencyMs: number;
	  }
	| {
			kind: "auth";
			ts: number;
			userId: string;
			event: "login" | "logout" | "refresh" | "denied";
			detail?: string;
	  };

export interface AuditStats {
	totalEvents: number;
	toolCalls: number;
	toolCallsByName: Array<{ toolName: string; count: number; failures: number }>;
	chatCompletions: number;
	oldestTs: number | null;
}

export interface UsageSnapshot {
	totals: {
		inputTokens: number;
		outputTokens: number;
		requestCount: number;
		estCostUsd: number;
	};
	last7Days: Array<{
		date: string;
		inputTokens: number;
		outputTokens: number;
		requestCount: number;
		estCostUsd: number;
	}>;
	byModel: Array<{
		model: string;
		inputTokens: number;
		outputTokens: number;
		requestCount: number;
	}>;
}

export interface BusinessDTO {
	id: string;
	name: string;
	tier: string;
	mode: string;
	revenue_year_k: number | null;
	dividends_year_k: number | null;
	margin_pct: number | null;
	stage: string;
	main_risk: string | null;
	notes: string | null;
	/** JSON-string holding sections/components/i18n/brandTone/paletteHints/notes
	 *  per the per-business contentPolicy contract (see head-of-design-review prompt). */
	contentPolicy: string | null;
	contentPolicyUpdatedAt: string | null;
	latest_status_gate: string | null;
	latest_status_runway_months: number | null;
	latest_status_summary: string | null;
	latest_status_last_review: string | null;
	latest_status_next_decision_due: string | null;
	latest_status_at: string | null;
}

export interface ContentPolicy {
	schemaVersion?: number;
	sections?: Record<string, boolean>;
	components?: Record<string, boolean>;
	i18n?: { locales?: string[]; default?: string };
	brandTone?: { style?: string; playful?: boolean; premium?: boolean; minimal?: boolean };
	paletteHints?: { darkSurface?: boolean; accent?: string; secondary?: string };
	notes?: string;
}

export const CONTENT_POLICY_SECTIONS = [
	"home",
	"casinos-list",
	"casino-detail",
	"bonuses",
	"news",
	"faq",
	"auth",
	"compare",
	"search",
	"user-reviews",
] as const;

export const CONTENT_POLICY_COMPONENTS = [
	"CasinoCard",
	"BonusCard",
	"RatingBlock",
	"TrustBadgeCluster",
	"ProConList",
	"FaqAccordion",
	"CtaButton",
	"NewsCard",
	"UserReviewsList",
	"CompareWidget",
] as const;

export const BRAND_TONE_STYLES = ["trust-led", "playful", "premium", "minimal"] as const;

export interface AgentVersions {
	prompt: string;
	agentMd: string;
	knowledge: string;
	agent: string;
}

export interface AgentInfo {
	key: string;
	displayName: string;
	parent: string | null;
	tasksProjectId: string;
	files: { systemPrompt: string; agentMd: string; knowledge: string };
	versions?: AgentVersions;
}

export interface BusinessProfile {
	business: BusinessDTO;
	agents: AgentInfo[];
	githubEditEnabled: boolean;
}

export interface FileFromGitHub {
	path: string;
	sha: string;
	content: string;
	htmlUrl: string;
	/**
	 * For prompt.ts files: true if the server stripped the TypeScript template
	 * literal wrapper (the UI then shows just the prompt body). The wrapper is
	 * preserved automatically on commit. false = whole file content, edit as-is.
	 */
	unwrapped: boolean;
	/**
	 * True when the content lives inline in a JSON registry (extras agents) and
	 * there's no TypeScript wrapper to worry about — UI should skip the wrapper
	 * warning even though unwrapped is false.
	 */
	inline?: boolean;
}

export interface ProposeEditResult {
	number: number;
	htmlUrl: string;
	branch: string;
	sha: string;
	/** True when the Worker auto-merged the PR right after opening it. */
	merged?: boolean;
	/** When auto-merge failed (CI gating, conflicts), GitHub's error message. */
	mergeError?: string;
}

export type FileKind = "prompt" | "agent_md" | "knowledge";

export interface AgentInstance {
	label: string;
	hostId: string;
	hostname: string;
	platform: string;
	arch: string;
	version: string;
	startedAt: number;
	lastSeen: number;
	pid?: number;
	online: boolean;
	/** Server-derived: error within 2min OR (online + no claim activity for 5min). */
	stuck: boolean;
	/** Last caught loop error (poll failure, MCP error) — surfaced as amber state. */
	lastError?: { message: string; ts: number; taskId?: string };
	/** Task currently being processed by this host (cleared on done/error). */
	currentTask?: { id: string; title: string; startedAt: number };
	/** Wall-clock of the most recent successful claim. */
	lastClaimAt?: number;
}

export interface AgentStatusForLabel {
	label: string;
	instances: AgentInstance[];
}

/** Response shape of GET /api/agents/status (without ?label filter). */
export interface AgentStatusResponse {
	statuses: AgentStatusForLabel[];
	/** Latest CLI version the Worker was built with. Hosts whose heartbeat
	 *  reports a different version should be flagged for restart. */
	expectedCliVersion?: string;
	/** Git SHA the Worker bundle was built from (first 8 chars of deploy commit).
	 *  Stamped at build time by .github/workflows/deploy.yml. "dev" in local mode. */
	deploySha?: string;
}

export interface AgentRuntime {
	taskTimeoutMs?: number;
	agentMaxCostUsd?: number;
}

export interface AgentDirectoryEntry {
	key: string;
	displayName: string;
	parent: string | null;
	tasksProjectId: string;
	businessId: string;
	businessName: string;
	files: { systemPrompt: string; agentMd: string; knowledge: string };
	versions?: AgentVersions;
	skills?: string[];
	runtime?: AgentRuntime | null;
}

export type SkillKind = "mcp-http" | "mcp-stdio" | "plugin" | "marketplace-plugin";
export type SkillOrigin = "builtin" | "external";

export type SkillAuth =
	| { kind: "none" }
	| { kind: "oidc-scope"; scope: string }
	| { kind: "literal-header"; header: string; value: string };

export interface McpHttpSkillConfig {
	url: string;
	auth: SkillAuth;
}
export interface McpStdioSkillConfig {
	command: string;
	args?: string[];
	env?: Record<string, string>;
}
export interface PluginSkillFile {
	path: string;
	content: string;
}
export interface PluginSkillConfig {
	files: PluginSkillFile[];
}
export interface MarketplacePluginSkillConfig {
	marketplace: string;
	name: string;
}

export interface Skill {
	key: string;
	displayName: string;
	description?: string;
	origin: SkillOrigin;
	kind: SkillKind;
	config: McpHttpSkillConfig | McpStdioSkillConfig | PluginSkillConfig | MarketplacePluginSkillConfig;
	containsSecret?: boolean;
	createdAt: string;
	createdBy: string;
	updatedAt?: string;
	updatedBy?: string;
}

export interface SkillWithUsage extends Skill {
	usageCount: number;
}

// =============================================================================
// Tools (granular tool catalog — separate from Skills which are the containers)
// =============================================================================

export type ToolSource =
	| { kind: "mcp"; skillKey: string; serverDisplayName: string }
	| { kind: "builtin" }
	| { kind: "orchestrator" }
	| { kind: "plugin"; skillKey: string };

export interface ToolAgentState {
	key: string;
	displayName: string;
	granted: boolean;
	allowed: boolean;
	denied: boolean;
}

export interface ToolEntry {
	fullKey: string;
	name: string;
	description: string | null;
	source: ToolSource;
	permissionRule: string | null;
	agents: ToolAgentState[];
}

export interface ToolsResponse {
	tools: ToolEntry[];
	errors?: Array<{ skillKey: string; message: string }>;
}

export type StreamEvent =
	| { type: "text_delta"; text: string }
	| { type: "tool_use_start"; toolName: string; toolUseId: string; input?: unknown }
	| {
			type: "tool_use_result";
			toolName: string;
			toolUseId: string;
			ok: boolean;
			previewBytes: number;
			preview?: string;
	  }
	| { type: "iteration"; index: number; reason: string }
	| { type: "done"; usage: { inputTokens: number; outputTokens: number }; model: string }
	| { type: "error"; code: string; message: string };

export const api = {
	chat: (payload: ChatRequest) =>
		request<ChatResponse>("/api/chat", {
			method: "POST",
			body: JSON.stringify(payload),
		}),

	getChatHistory: () =>
		request<{ messages: Array<{ role: "user" | "assistant"; content: string }> }>(
			"/api/chat/history",
		),

	clearChatHistory: () =>
		request<{ ok: true }>("/api/chat/history", { method: "DELETE" }),

	listDecisions: (params?: { business?: string; dueOnly?: boolean; limit?: number }) => {
		const qs = new URLSearchParams();
		if (params?.business) qs.set("business", params.business);
		if (params?.dueOnly) qs.set("due_only", "true");
		if (params?.limit) qs.set("limit", String(params.limit));
		const path = qs.toString() ? `/api/decisions?${qs}` : "/api/decisions";
		return request<{ decisions: DecisionDTO[]; count: number }>(path);
	},

	listBusinesses: () => request<{ businesses: BusinessDTO[] }>("/api/businesses"),

	getBusinessProfile: (id: string) =>
		request<BusinessProfile>(`/api/businesses/${encodeURIComponent(id)}`),

	updateBusiness: (
		id: string,
		patch: {
			name?: string;
			stage?: string;
			main_risk?: string | null;
			notes?: string | null;
			contentPolicy?: string | null;
		},
	) =>
		request<{ business: Partial<BusinessDTO> }>(`/api/businesses/${encodeURIComponent(id)}`, {
			method: "PATCH",
			body: JSON.stringify(patch),
		}),

	createBusiness: (input: {
		id: string;
		name: string;
		tier: string;
		mode?: string;
		stage?: string;
		main_risk?: string | null;
		notes?: string | null;
		revenue_year_k?: number | null;
		dividends_year_k?: number | null;
		margin_pct?: number | null;
	}) =>
		request<{ business: BusinessDTO }>("/api/businesses", {
			method: "POST",
			body: JSON.stringify(input),
		}),

	getAgentFile: (businessId: string, agentKey: string, kind: FileKind) =>
		request<FileFromGitHub>(
			`/api/businesses/${encodeURIComponent(businessId)}/agents/${encodeURIComponent(agentKey)}/file?kind=${kind}`,
		),

	proposeAgentFileEdit: (
		businessId: string,
		agentKey: string,
		body: { kind: FileKind; sha: string; content: string; commitMessage: string },
	) =>
		request<ProposeEditResult>(
			`/api/businesses/${encodeURIComponent(businessId)}/agents/${encodeURIComponent(agentKey)}/file`,
			{ method: "POST", body: JSON.stringify(body) },
		),

	getAgentStatus: async (label: string) => {
		const data = await request<{ statuses: AgentStatusForLabel[] }>(
			`/api/agents/status?label=${encodeURIComponent(label)}`,
		);
		const entry = data.statuses.find((s) => s.label === label);
		return { label, instances: entry?.instances ?? [] };
	},

	listAgents: () => request<{ agents: AgentDirectoryEntry[] }>("/api/agents"),

	createAgent: (input: {
		key: string;
		displayName: string;
		businessId: string;
		parent?: string | null;
		tasksProjectId: string;
		systemPrompt: string;
		agentMd: string;
	}) =>
		request<ProposeEditResult>("/api/agents", {
			method: "POST",
			body: JSON.stringify(input),
		}),

	getAgent: (key: string) =>
		request<{
			agent: AgentDirectoryEntry;
			claudeSettings: Record<string, unknown> | null;
			githubEditEnabled: boolean;
		}>(`/api/agents/${encodeURIComponent(key)}`),

	listAllAgentStatuses: () => request<AgentStatusResponse>("/api/agents/status"),

	setAgentSkills: (key: string, skills: string[]) =>
		request<ProposeEditResult | { noop: true; skills: string[] }>(
			`/api/agents/${encodeURIComponent(key)}/skills`,
			{ method: "PUT", body: JSON.stringify({ skills }) },
		),

	setAgentClaudeSettings: (key: string, settings: Record<string, unknown> | null) =>
		request<ProposeEditResult | { noop: true }>(
			`/api/agents/${encodeURIComponent(key)}/claude-settings`,
			{ method: "PUT", body: JSON.stringify({ settings }) },
		),

	setAgentRuntime: (key: string, runtime: AgentRuntime | null) =>
		request<ProposeEditResult | { noop: true }>(
			`/api/agents/${encodeURIComponent(key)}/runtime`,
			{ method: "PUT", body: JSON.stringify({ runtime }) },
		),

	reopenAgentTask: (agentKey: string, taskId: string) =>
		request<{ ok: true; taskId: string; agent: string }>(
			`/api/agents/${encodeURIComponent(agentKey)}/tasks/${encodeURIComponent(taskId)}/reopen`,
			{ method: "POST" },
		),

	listSkills: () => request<{ skills: SkillWithUsage[] }>("/api/skills"),

	getSkill: (key: string) =>
		request<{ skill: Skill; usageCount: number }>(`/api/skills/${encodeURIComponent(key)}`),

	createSkill: (input: {
		key: string;
		displayName: string;
		description?: string;
		origin: SkillOrigin;
		kind: SkillKind;
		config:
			| McpHttpSkillConfig
			| McpStdioSkillConfig
			| PluginSkillConfig
			| MarketplacePluginSkillConfig;
		containsSecret?: boolean;
	}) =>
		request<ProposeEditResult>("/api/skills", {
			method: "POST",
			body: JSON.stringify(input),
		}),

	updateSkill: (
		key: string,
		patch: {
			displayName?: string;
			description?: string | null;
			config?:
				| McpHttpSkillConfig
				| McpStdioSkillConfig
				| PluginSkillConfig
				| MarketplacePluginSkillConfig;
			containsSecret?: boolean;
		},
	) =>
		request<ProposeEditResult>(`/api/skills/${encodeURIComponent(key)}`, {
			method: "PATCH",
			body: JSON.stringify(patch),
		}),

	deleteSkill: (key: string) =>
		request<ProposeEditResult>(`/api/skills/${encodeURIComponent(key)}`, {
			method: "DELETE",
		}),

	listTools: () => request<ToolsResponse>("/api/tools"),

	getTool: (fullKey: string) =>
		request<{ tool: ToolEntry }>(`/api/tools/${encodeURIComponent(fullKey)}`),

	usage: () => request<UsageSnapshot>("/api/usage"),

	audit: (params?: { kinds?: string[]; limit?: number }) => {
		const qs = new URLSearchParams();
		if (params?.kinds && params.kinds.length > 0) qs.set("kinds", params.kinds.join(","));
		if (params?.limit) qs.set("limit", String(params.limit));
		const path = qs.toString() ? `/api/audit?${qs}` : "/api/audit";
		return request<{ events: AuditEvent[]; count: number }>(path);
	},

	auditStats: () => request<AuditStats>("/api/audit/stats"),

	listDigests: () =>
		request<{ digests: Array<{ id: string; ts: string; summary: string; body: string; inputTokens: number; outputTokens: number; estCostUsd: number }> }>(
			"/api/digest",
		),

	runDigestNow: () =>
		request<{ ts: string; body: string; model: string; inputTokens: number; outputTokens: number; estCostUsd: number }>(
			"/api/digest/run-now",
			{ method: "POST", body: JSON.stringify({}) },
		),

	/**
	 * Streaming chat. Returns an async iterator over SSE events. The generator
	 * handles SSE framing parsing; consumer just gets typed StreamEvents.
	 */
	async *chatStream(payload: ChatRequest): AsyncGenerator<StreamEvent, void, unknown> {
		const res = await fetch("/api/chat/stream", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(payload),
		});

		if (res.status === 401) {
			login();
			throw new Error("not_authenticated");
		}

		if (!res.ok || !res.body) {
			const text = await res.text();
			throw new ApiError(res.status, text.slice(0, 200));
		}

		const reader = res.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";

		try {
			while (true) {
				const { value, done } = await reader.read();
				if (done) break;
				buffer += decoder.decode(value, { stream: true });

				let sep = buffer.indexOf("\n\n");
				while (sep !== -1) {
					const frame = buffer.slice(0, sep);
					buffer = buffer.slice(sep + 2);
					sep = buffer.indexOf("\n\n");

					const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
					if (!dataLine) continue;
					const json = dataLine.slice(5).trim();
					if (!json) continue;
					try {
						const event = JSON.parse(json) as StreamEvent;
						yield event;
					} catch {
						// ignore malformed
					}
				}
			}
		} finally {
			reader.releaseLock();
		}
	},
};

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
	) {
		super(message);
		this.name = "ApiError";
	}
}
